/**
 * Canvas AI-assist mappers(DTO → camelCase domain;ADR-026)
 * -----------------------------------------------------------------------------
 * placement nodes/edges 複用 dag 的 `mapCreativeNodeDto` / `mapNodeEdgeDto`(**單一 mapper
 * 鏈** — DB Optimizer 紅線:畫進 canvas 必走同一條,短路徑產不同 shape → renderer 靜默壞)。
 * 邊界一律 `?? []` / `?? null` normalize(對齊 R41.0 mapper-edge 防漂值契約)。
 */

import type {
  CopyAssetsDto,
  CopyAssistResultDto,
  CopyCategoryReportDto,
  CopyValidationReportDto,
  CopyVariantDto,
  PlacementExpandResultDto,
  TextGenResultDto,
} from './canvasAi.dtos';
import type {
  CopyAssetCategory,
  CopyAssets,
  CopyAssistResult,
  CopyCategoryReport,
  CopyValidationReport,
  CopyVariant,
  PlacementExpandResult,
  TextGenResult,
} from './canvasAi.types';
import {
  mapCreativeNodeDto,
  mapNodeEdgeDto,
  unwrapEdgeEnvelope,
  unwrapNodeEnvelope,
} from './dag';

export function mapCopyVariantDto(dto: CopyVariantDto): CopyVariant {
  return {
    angleId: dto.angle_id,
    status: dto.status,
    text: dto.text ?? null,
    errorClass: dto.error_class ?? null,
    credits: dto.credits ?? null,
  };
}

export function mapCopyAssistResultDto(dto: CopyAssistResultDto): CopyAssistResult {
  return {
    requestId: dto.request_id,
    variants: (dto.variants ?? []).map(mapCopyVariantDto),
    chargedCredits: dto.charged_credits ?? 0,
  };
}

export function mapPlacementExpandResultDto(dto: PlacementExpandResultDto): PlacementExpandResult {
  // nodes/edges 走 dag 共用 unwrap + mapper 鏈(相容裸 DTO + `{node}`/`{edge}` 業務 envelope)。
  return {
    requestId: dto.request_id,
    placementId: dto.placement_id,
    nodes: (dto.nodes ?? []).map(unwrapNodeEnvelope).map(mapCreativeNodeDto),
    edges: (dto.edges ?? []).map(unwrapEdgeEnvelope).map(mapNodeEdgeDto),
  };
}

// ─── AI-1 ideation ───────────────────────────────────────────────────────────



// ─── AI-5 text-gen ─────────────────────────────────────────────────────────────

/** `CopyAssets` DTO → domain(各多則欄 `?? []`;company_name 單值 `?? ''`)。 */
export function mapCopyAssetsDto(dto: CopyAssetsDto | null | undefined): CopyAssets {
  return {
    pmaxShortTitle: dto?.pmax_short_title ?? [],
    shortTitle: dto?.short_title ?? [],
    longTitle: dto?.long_title ?? [],
    description: dto?.description ?? [],
    cta: dto?.cta ?? [],
    companyName: dto?.company_name ?? '',
    displayPath: dto?.display_path ?? [],
  };
}

function mapCopyCategoryReportDto(dto: CopyCategoryReportDto): CopyCategoryReport {
  return {
    kept: dto.kept,
    truncated: dto.truncated ?? 0,
    droppedOverMax: dto.dropped_over_max ?? 0,
    droppedOverLimit: dto.dropped_over_limit ?? 0,
    belowMin: dto.below_min ?? false,
    belowSoftMin: dto.below_soft_min ?? false,
    hintUnmet: dto.hint_unmet ?? false,
  };
}

export function mapCopyValidationReportDto(
  dto: CopyValidationReportDto | null | undefined,
): CopyValidationReport {
  const categories: Partial<Record<CopyAssetCategory, CopyCategoryReport>> = {};
  for (const [key, value] of Object.entries(dto?.categories ?? {})) {
    if (value) categories[key as CopyAssetCategory] = mapCopyCategoryReportDto(value);
  }
  return { categories };
}

export function mapTextGenResultDto(dto: TextGenResultDto): TextGenResult {
  return {
    requestId: dto.request_id,
    assets: mapCopyAssetsDto(dto.assets),
    report: mapCopyValidationReportDto(dto.report),
    chargedCredits: dto.charged_credits ?? 0,
    // R270 — 缺席與 null 都收斂成 null(見 domain 型別註解:兩者處置相同)。
    blueCardNodeId: dto.blue_card_node_id ?? null,
  };
}

/**
 * `CopyAssets`(domain camelCase)→ DTO(snake_case)。持久化用:寫進 OUTPUT node
 * `config.copy_assets`(對齊後端 `CopyAssets` schema key,reload 走 `mapCopyAssetsDto` 還原)。
 */
export function serializeCopyAssets(a: CopyAssets): CopyAssetsDto {
  return {
    pmax_short_title: a.pmaxShortTitle,
    short_title: a.shortTitle,
    long_title: a.longTitle,
    description: a.description,
    cta: a.cta,
    company_name: a.companyName,
    display_path: a.displayPath,
  };
}
